import React, { Component } from "react";

import styled from "styles/styled_components";
import { map, always, equals } from "ramda";
import { Maybe } from "true-myth";

import DropdownOption from "components/shared/dropdown_option";
import { DROPDOWN_LABEL_POSITION } from "components/shared/labeled_dropdown";
import { noopWhenDisabled } from "helpers/general";

function defaultKeyFormatter<T>(option: Maybe<IOption<T>>): string {
  return option.map(opt => `${opt.label}-${opt.id}`).unwrapOr("placeholder");
}

export interface IOption<T> {
  id: string;
  label: string;
  meta: T;
}

export interface IDropdownProps<T> {
  currentOption?: IOption<T>;
  disabled?: boolean;
  dropdownName: string; // For aria labelling
  errorMessage?: string;
  keyFormatter?: (option: Maybe<IOption<T>>) => string;
  onOptionClick: (option: Maybe<IOption<T>>) => void;
  options: Array<IOption<T>>;
  size?: DROPDOWN_SIZE;
  labelPosition?: DROPDOWN_LABEL_POSITION;
}

export enum DROPDOWN_SIZE {
  SMALL = "SMALL",
  LARGE = "LARGE",
  FULL = "FULL",
}

export interface IDropdownState<T> {
  expanded: boolean;
  currentOption: Maybe<IOption<T>>;
}

export interface IStyledDropdownProps {
  isExpanded: boolean;
  disabled: boolean;
}

export const StyledDropdown = styled("ul")<IStyledDropdownProps>`
  background: ${props =>
    props.disabled
      ? props.theme.colors.dsGrayLight
      : props.theme.colors.brandWhite};
  border: ${props => `1px solid ${props.theme.colors.brandGray10}`};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => (props.isExpanded ? props.theme.shadows.small : "")};
  color: ${props => props.theme.colors.brandGray40};
  display: inline-block;
  left: 0;
  margin-bottom: ${props => props.theme.spacing.small};
  max-height: ${props => `${18 * props.theme.rawGridBase}px`};
  ${({ disabled }) => disabled && "opacity: 0.6;"}
  overflow-x: hidden;
  overflow-y: ${props => (props.isExpanded ? "scroll" : "hidden")};
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: ${props => (props.isExpanded ? props.theme.layering.dropdown : "")};
`;

export interface IStyledDropdownWrapperProps {
  size: DROPDOWN_SIZE;
  labelPosition: DROPDOWN_LABEL_POSITION;
}

export const StyledDropdownWrapper = styled("div")<IStyledDropdownWrapperProps>`
  display: block;
  height: ${props => `calc(4 * ${props.theme.gridBase})`};
  margin: ${props =>
    props.labelPosition === DROPDOWN_LABEL_POSITION.LEFT
      ? `0 0 0 ${props.theme.spacing.medium} `
      : `${props.theme.spacing.xsmall} 0`};
  position: relative;
  width: ${props => {
    switch (props.size) {
      case DROPDOWN_SIZE.SMALL:
        return `calc(11 * ${props.theme.gridBase})`;

      case DROPDOWN_SIZE.FULL:
        return `calc(100% - ${props.theme.gridBase})`;

      case DROPDOWN_SIZE.LARGE:
      default:
        return `calc(23 * ${props.theme.gridBase})`;
    }
  }};
`;

export const StyledError = styled.div`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.text.size.base};
  line-height: ${props => `calc(2 * ${props.theme.gridBase})`};
  margin: ${props =>
    `calc(4.5 * ${props.theme.gridBase}) ${props.theme.spacing.xsmall} ${
      props.theme.spacing.small
    }`};
`;

export default class Dropdown<T> extends Component<
  IDropdownProps<T>,
  IDropdownState<T>
> {
  constructor(props: IDropdownProps<T>) {
    super(props);

    this.selectedOption = null;
    this.toggleOptions = this.toggleOptions.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.scrollToCurrentOption = this.scrollToCurrentOption.bind(this);

    this.state = {
      expanded: false,
      currentOption: Maybe.of<IOption<T>>(props.currentOption),
    };

    this.selectedOptionRef = (element: HTMLLIElement) => {
      this.selectedOption = element;
    };
    this.node = React.createRef();
    this.dropdownParent = React.createRef();
  }

  private node: React.RefObject<HTMLDivElement>;
  private dropdownParent: React.RefObject<HTMLUListElement>;
  private selectedOption: HTMLLIElement | null;
  private selectedOptionRef: (element: HTMLLIElement) => void;

  public componentDidMount() {
    document.addEventListener("click", this.handleClickOutside, true);
  }

  public componentWillUnmount() {
    document.removeEventListener("click", this.handleClickOutside, true);
  }

  private toggleOptions() {
    const expanded = !this.state.expanded;
    this.setState({ expanded });
  }

  private handleOptionClick(option: Maybe<IOption<T>>) {
    this.updateCurrentOption(option);
    this.props.onOptionClick(option);
  }

  private updateCurrentOption(option: Maybe<IOption<T>>) {
    this.setState({
      expanded: false,
      currentOption: option,
    });
  }

  private scrollToCurrentOption() {
    const { selectedOption, dropdownParent } = this;
    if (selectedOption && dropdownParent.current) {
      dropdownParent.current.scrollTop = selectedOption.offsetTop;
    }
  }

  public componentDidUpdate(prevProps: IDropdownProps<T>) {
    const { currentOption } = this.props;
    if (currentOption !== prevProps.currentOption) {
      this.updateCurrentOption(Maybe.of(currentOption));
    }
    this.scrollToCurrentOption();
  }

  public handleClickOutside(event: Event) {
    const { target } = event;
    if (
      target instanceof Element &&
      this.node.current &&
      !this.node.current.contains(target)
    ) {
      this.setState({ expanded: false });
    }
  }

  public render() {
    const {
      disabled = false,
      dropdownName,
      errorMessage,
      keyFormatter,
      options,
      size = DROPDOWN_SIZE.LARGE,
      labelPosition = DROPDOWN_LABEL_POSITION.LEFT,
    } = this.props;
    const { expanded, currentOption } = this.state;
    let renderedOptions;
    if (expanded) {
      renderedOptions = map(opt => {
        const option = Maybe.of(opt);
        const isCurrentOption: boolean = currentOption
          .map(current => equals(opt.id, current.id))
          .unwrapOr(false);

        return (
          <DropdownOption
            createOptionRef={this.selectedOptionRef}
            isExpanded={expanded}
            isSelected={isCurrentOption}
            key={
              keyFormatter ? keyFormatter(option) : defaultKeyFormatter(option)
            }
            option={option}
            {...this.props}
            onOptionClick={this.handleOptionClick}
          />
        );
      }, options);
    } else {
      // Try to find a real option with the same id
      // (this is good in case currentOption goes out of sync with the provided options)
      const selected = currentOption.map(
        current =>
          options.find(opt => opt.id === current.id) ||
          currentOption.unsafelyUnwrap()
      );
      renderedOptions = (
        <DropdownOption
          createOptionRef={this.selectedOptionRef}
          isExpanded={expanded}
          isSelected={true}
          key={
            keyFormatter
              ? keyFormatter(currentOption)
              : defaultKeyFormatter(currentOption)
          }
          option={selected}
          onOptionClick={always}
        />
      );
    }

    let renderedErrorMessage;
    if (errorMessage) {
      renderedErrorMessage = (
        <StyledError id={`${dropdownName}-error`} className="error-text">
          {errorMessage}
        </StyledError>
      );
    }

    return (
      <StyledDropdownWrapper
        size={size}
        ref={this.node}
        labelPosition={labelPosition}>
        <StyledDropdown
          ref={this.dropdownParent}
          role="listbox"
          aria-describedby={`${dropdownName}-error`}
          disabled={disabled}
          isExpanded={expanded}
          onClick={noopWhenDisabled(this.toggleOptions, disabled)}
          tabIndex={0}>
          {renderedOptions}
        </StyledDropdown>
        {renderedErrorMessage}
      </StyledDropdownWrapper>
    );
  }
}
